// apps/web/src/features/clients/useCopyText.ts
//
// Copy-to-clipboard for the client workspace (AC-6). Managers paste requisites
// into invoices and bank forms, so the copied string is the ORIGINAL text —
// line breaks and all — never a re-flowed preview.
//
// WHY the failure text is returned instead of only toasted: a denied clipboard
// permission (Safari without a user gesture, hardened corporate profiles) is
// common enough that "copy failed" alone is a dead end. The caller reveals the
// exact string as selectable text so the operator can still take it manually.
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useToastStore } from '../../stores/toast.js';

export interface UseCopyTextResult {
  copy: (text: string) => Promise<void>;
  /** Text of the last failed copy, for the selectable fallback. */
  failedText: string | null;
  dismissFailure: () => void;
}

export function useCopyText(): UseCopyTextResult {
  const { t } = useTranslation(['clients']);
  const show = useToastStore((s) => s.show);
  const [failedText, setFailedText] = useState<string | null>(null);

  const copy = useCallback(
    async (text: string) => {
      try {
        // `navigator.clipboard` is absent on insecure origins; treat that the
        // same as a denied write rather than throwing an unhandled TypeError.
        const clipboard = navigator.clipboard as Clipboard | undefined;
        if (!clipboard) throw new Error('clipboard unavailable');
        await clipboard.writeText(text);
        setFailedText(null);
        show({ tone: 'success', text: t('clients:copy.success') });
      } catch {
        setFailedText(text);
        show({ tone: 'error', text: t('clients:copy.failed') });
      }
    },
    [show, t],
  );

  const dismissFailure = useCallback(() => setFailedText(null), []);

  return { copy, failedText, dismissFailure };
}
